/**
 * BotMacros - Service for handling bot macro commands
 * 
 * Provides a system for executing predefined commands that start with "/command"
 * These commands can generate test messages, perform actions, or inject content.
 * 
 * Follows ADR principles:
 * - ADR-004: Separation of Concerns - Business logic layer
 * - ADR-005: Information Hiding - Clean interface, implementation hidden
 * - ADR-003: High Cohesion - Single responsibility: command execution
 */

import type { MessageInterface } from '../hooks/useMessageProcessing';

/**
 * Result of executing a bot macro command
 */
export interface BotMacroResult {
  /** Whether the command was recognized and executed */
  handled: boolean;
  /** Messages to inject into the chat (if any) */
  messages?: MessageInterface[];
  /** Error message if command failed */
  error?: string;
  /** Whether the original command message should be sent to the backend (default: false) */
  sendToBackend?: boolean;
}

/**
 * Command handler function signature
 * @param args - Command arguments (everything after the command name)
 * @returns BotMacroResult indicating success and any messages to inject
 */
type CommandHandler = (args: string[]) => BotMacroResult;

/**
 * Registry of available commands
 */
const commandHandlers = new Map<string, CommandHandler>();

/**
 * Registers a new command handler
 * 
 * @param commandName - Name of the command (without /command prefix)
 * @param handler - Function that handles the command
 */
export function registerCommand(commandName: string, handler: CommandHandler): void {
  commandHandlers.set(commandName.toLowerCase(), handler);
}

/**
 * Checks if a message is a bot macro command
 * 
 * @param message - The message text to check
 * @returns True if the message starts with "/command"
 */
export function isBotMacroCommand(message: string): boolean {
  return message.trim().startsWith('/command');
}

/**
 * Parses a bot macro command into command name and arguments
 * 
 * @param message - The full command message (e.g., "/command test-layout")
 * @returns Object with command name and arguments array
 */
export function parseCommand(message: string): { command: string; args: string[] } {
  const trimmed = message.trim();
  if (!trimmed.startsWith('/command')) {
    return { command: '', args: [] };
  }

  const parts = trimmed.slice('/command'.length).trim().split(/\s+/);
  const command = parts[0] || '';
  const args = parts.slice(1);

  return { command: command.toLowerCase(), args };
}

/**
 * Executes a bot macro command
 * 
 * @param message - The full command message
 * @returns BotMacroResult with execution result
 */
export function executeCommand(message: string): BotMacroResult {
  if (!isBotMacroCommand(message)) {
    return { handled: false };
  }

  const { command, args } = parseCommand(message);

  if (!command) {
    return {
      handled: true,
      error: 'No command specified. Usage: /command <command-name> [args...]',
      sendToBackend: false
    };
  }

  const handler = commandHandlers.get(command);
  if (!handler) {
    return {
      handled: true,
      error: `Unknown command: ${command}. Available commands: ${Array.from(commandHandlers.keys()).join(', ')}`,
      sendToBackend: false
    };
  }

  try {
    return handler(args);
  } catch (error) {
    return {
      handled: true,
      error: `Error executing command: ${error instanceof Error ? error.message : 'Unknown error'}`,
      sendToBackend: false
    };
  }
}

/**
 * Creates a test message with the specified content
 * 
 * @param content - Message content
 * @param source - Message source (default: 'ai_agent')
 * @returns MessageInterface
 */
function createTestMessage(
  content: string,
  source: 'ai_agent' | 'customer' = 'ai_agent'
): MessageInterface {
  return {
    id: `test-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    kind: 'message',
    source,
    creationUtc: new Date(),
    offset: Date.now(),
    correlationId: `test-${Date.now()}`,
    data: { message: content },
    status: 'ready',
  };
}

/**
 * Test layout command handler
 * Generates a series of test messages demonstrating markdown, links, and button variants
 * This command does NOT send the original message to the backend - it's purely frontend
 */
function handleTestLayout(): BotMacroResult {
  const messages: MessageInterface[] = [];

  // Message 1: Basic markdown formatting
  messages.push(createTestMessage(
    `**Markdown Formatting Test**

This message demonstrates various markdown features:
- **Bold text** should be very bold
- *Italic text* should be slanted
- Regular text with normal spacing

**Lists:**
- First item
- Second item
- Third item

**Numbered lists:**
1. First numbered item
2. Second numbered item
3. Third numbered item`
  ));

  // Message 2: Links
  messages.push(createTestMessage(
    `**Link Test**

Here are some links:
- [Regular link to Google](https://www.google.com)
- [Link to Parlant](https://www.parlant.io)
- [Link with longer text that wraps](https://www.example.com/very/long/path/to/resource)

Links should be clickable and open in a new tab.`
  ));

  // Message 3: Button variants - default
  messages.push(createTestMessage(
    `**Button Variants - Default**

Default button styling:
[btn:Default Button](action:default)`
  ));

  // Message 4: Button variants - primary
  messages.push(createTestMessage(
    `**Button Variants - Primary**

Primary button:
[btn:Primary Button:primary](action:primary)`
  ));

  // Message 5: Button variants - success/accept/yes
  messages.push(createTestMessage(
    `**Button Variants - Success**

Success buttons (green):
[btn:Success:success](action:success) [btn:Accept:accept](action:accept) [btn:Yes:yes](action:yes)`
  ));

  // Message 6: Button variants - danger/reject/no
  messages.push(createTestMessage(
    `**Button Variants - Danger**

Danger buttons (red):
[btn:Danger:danger](action:danger) [btn:Reject:reject](action:reject) [btn:No:no](action:no)`
  ));

  // Message 7: Button variants - warning/maybe
  messages.push(createTestMessage(
    `**Button Variants - Warning**

Warning buttons (amber/yellow):
[btn:Warning:warning](action:warning) [btn:Maybe:maybe](action:maybe)`
  ));

  // Message 8: Button variants - info and neutral
  messages.push(createTestMessage(
    `**Button Variants - Info & Neutral**

Info and neutral buttons:
[btn:Info:info](action:info) [btn:Neutral:neutral](action:neutral)`
  ));

  // Message 9: Mixed content
  messages.push(createTestMessage(
    `**Mixed Content Test**

This message combines **bold text**, [regular links](https://example.com), and buttons:

**Actions:**
[btn:Approve:success](action:approve) [btn:Reject:danger](action:reject) [btn:Maybe Later:warning](action:maybe)

**Resources:**
- [Documentation](https://docs.example.com)
- [Support](https://support.example.com)

**Note:** All formatting should render correctly with proper spacing.

Sources:

[https://example.com/really/long/path/to/resources-and-learning-module-absolutely-extraneous-query-param-1-2-3-4-5-abcdef-ghijklmnopqrstuvwxyz-abcdefghijklmnopqrstuvwxyz-1234567890-abcdefghijklmnopqrstuvwxyz-abcdefghijklmnopqrstuvwxyz-1234567890-blorgl?ref=reference-point&rid=superlong1234567890abcdef](https://example.com/really/long/path/to/resources-and-learning-module-absolutely-extraneous-query-param-1-2-3-4-5-abcdef-ghijklmnopqrstuvwxyz-abcdefghijklmnopqrstuvwxyz-1234567890-abcdefghijklmnopqrstuvwxyz-abcdefghijklmnopqrstuvwxyz-1234567890-blorgl?ref=reference-point&rid=superlong1234567890abcdef)

[https://example.com/resources/dialogues/regular-session-overview-specifically-unnecessarily-lengthy-link-that-keeps-going-and-going-and-going-unlike-any-realistic-link-you-would-use-in-practice-superfluous-end.html?a=b&x=1234567890987654321qwertyuiopasdfghjklzxcvbnm](https://example.com/resources/dialogues/regular-session-overview-specifically-unnecessarily-lengthy-link-that-keeps-going-and-going-and-going-unlike-any-realistic-link-you-would-use-in-practice-superfluous-end.html?a=b&x=1234567890987654321qwertyuiopasdfghjklzxcvbnm)

[https://example.com/topic/models/bazkoof-hyper-extended-demonstration-of-link-capabilities-testing-url-parameter-excessiveness-to-the-absolute-limit-this-should-break-no-renderer-ever-modeled-this-length-surely.html#withEvenMoreFrag](https://example.com/topic/models/bazkoof-hyper-extended-demonstration-of-link-capabilities-testing-url-parameter-excessiveness-to-the-absolute-limit-this-should-break-no-renderer-ever-modeled-this-length-surely.html#withEvenMoreFrag)

`
));

  return {
    handled: true,
    messages,
    sendToBackend: false // Don't send test-layout command to backend
  };
}

// Register the test-layout command
registerCommand('test-layout', handleTestLayout);

/**
 * Gets a list of all available commands
 * 
 * @returns Array of command names
 */
export function getAvailableCommands(): string[] {
  return Array.from(commandHandlers.keys());
}

